import { Box, Button, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useNavigate, useParams } from "react-router-dom";
import { InventoryProfileService } from "subModule/src/core/services";
import { StockReceiptResponse } from "subModule/src/services/inventory/interface";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { selectUserInfo } from "features/user/userSlice";
const PrintPurchaseOrderStockReceived: React.FC = () => {
    const params = useParams();
    const { t } = useTranslation();
    const branding = useSelector(brandingInfo);
    const navigate = useNavigate();
    const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
    const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState<boolean>(true);
    const [sr, setSR] = useState<StockReceiptResponse>();
    const columns = [
        { id: 'itemId' },
        { id: 'itemCode'},
        { id: 'unitId' },
        { id: 'itemName', label: t('Item'), numeric: false, disablePadding: false, type: 'text' },
        { id: 'supplierSKU', label: t('SKU'), numeric: false, disablePadding: false, type: 'text' },
        { id: 'quantity', label: t('Quantity'), numeric: false, disablePadding: false, type: 'number' },
        { id: 'unitTitle', label: t('unit') },
        { id: 'unitPrice', label: t('Cost'), numeric: false, disablePadding: false, type: 'number' },
        { id: 'discount', label: `${t('Discount')} %`, numeric: false, disablePadding: false, type: 'number' },
        { id: 'batchNumber', label: t('Batch'), numeric: false, disablePadding: false, type: 'text' },
        { id: 'receivedDate', label: t('Received') + ' ' + t('Date'), numeric: false, disablePadding: false, type: 'date' },
        { id: 'expiryDate', label: t("Expiry") + ' ' + t('Date'), numeric: false, disablePadding: false, type: 'date' },
    ];
    useEffect(() => {
        if(loading) getStockReceiptById();
    }, []);
    const getStockReceiptById = () => {
        InventoryProfileService.getEntityById(params?.id, 'stockreceipt/bypoid').then((res: any) => {
            if(res.success) {
                const allReceipts = res.data || [];
                const mergedItems = allReceipts.flatMap((r: any) => r.stockReceiptItems || [])
                    .reduce((acc: any[], item: any) => {
                        const existing = acc.find(i => i.itemId === item.itemId);
                        if (existing) {
                            existing.quantity += item.quantity || 0;
                            if (item.batchNumber) {
                                existing.batchNumber = existing.batchNumber
                                    ? `${existing.batchNumber}, ${item.batchNumber}`
                                    : item.batchNumber;
                            }
                            existing.receivedDate = existing.receivedDate
                                ? `${existing.receivedDate}, ${item.receivedDate?.split('T')[0]}`
                                : item.receivedDate?.split('T')[0];
                            existing.expiryDate = existing.expiryDate
                                ? `${existing.expiryDate}, ${item.expiryDate?.split('T')[0]}`
                                : item.expiryDate?.split('T')[0];
                        } else {
                            acc.push({ ...item });
                        }
                        return acc;
                    }, []);

                const mergedSR = {
                    ...allReceipts[0],
                    // vendorTitle: allReceipts.map((r: any) => r.vendorTitle).join(', '),
                    // storeTitle: allReceipts.map((r: any) => r.storeTitle).join(', '),
                    stockNumber: allReceipts.map((r: any) => r.stockNumber).join(', '),
                    receivedDate: allReceipts.map((r: any) => r.receivedDate?.split('T')[0]).join(', '),
                    // totalQuantity: allReceipts.reduce((sum: number, r: any) => sum + (r.totalQuantity || 0), 0),
                    totalReceivedQuantity: allReceipts.reduce((sum: number, r: any) => sum + (r.totalReceivedQuantity || 0), 0),
                    totalAmount: allReceipts.reduce((sum: number, r: any) => sum + (r.totalAmount || 0), 0),
                    stockReceiptItems: mergedItems
                };
                setSR(mergedSR);
                setLoading(false);
            } else {
                setLoading(false);
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err:any) => { console.error("===", err); });
    }
    return (
        loading ? <Loader /> : <Box>
            <Box className='max-w-[1100px] bg-white py-[10px] px-[20px] rounded-[8px] block mx-auto hide-on-print mb-4'>
                <Box className='flex justify-between items-center' id='form'>
                    <Typography className="font-Saira text-[12px] font-semibold" style={{ color: themeColors?.primary }}>{t('Stock Received')}</Typography>
                    <Box className='flex gap-2'>
                        <Button className="secondary" variant="contained" style={{ color: themeColors?.primary }} disableElevation onClick={() => navigate(-1)}>{t('back')}</Button>
                        <Button className="primary" variant="contained" style={{ background: themeColors?.primary }} disableElevation onClick={() => window.print()}>{t('Print')}</Button>
                    </Box>
                </Box>
            </Box>
            <Box id="print-root" className='max-w-[1100px] bg-white p-[20px] rounded-[8px] block mx-auto'>
                <Box className='flex justify-between mb-2'>
                    <Typography className="font-Saira text-[12px] text-left">
                        <span className="italic font-medium">{t('Supplier')}: </span> 
                        <span className="font-bold">{sr?.vendorTitle || ''}</span>
                    </Typography>
                    <Box>
                        <Box className='block py-1 px-2 rounded-[8px]' style={{ background: themeColors?.appBg }}>
                            <Typography className="font-Saira text-[12px]">
                                <span className="font-bold" style={{ color: themeColors?.secondary }}>{t('Stock')} # </span> 
                                <span className="font-semibold">{sr?.stockNumber || ''}</span>
                            </Typography>
                        </Box>
                        <Typography className="font-Saira text-[12px] text-end font-semibold">
                            <span className="italic font-medium">{t('receivedDate')}</span>
                            <span>: </span>
                            <span>{sr?.receivedDate?.split('T')[0] || ''}</span>
                        </Typography>
                    </Box>
                </Box>
                <Box className='flex justify-between py-3 px-2 rounded-[8px] mb-2' style={{ background: themeColors?.appBg }}>
                    <Typography className="font-Saira text-[12px] text-left">
                        <span className="italic font-medium">{t('store')}: </span> 
                        <span className="font-bold">{sr?.storeTitle || ''}</span>
                    </Typography>
                </Box>
                <Typography className="font-Saira text-[12px] font-semibold text-[#333843] text-left mb-2">{t('Stock Received')}</Typography>
                <Box className='mb-4 w-[100%]' id="inventorytable">
                    <TableContainer>
                        <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={'small'}>
                            <TableHead>
                                <TableRow sx={{ borderBottom: `1px solid #B8BDCB` }}>
                                    {columns.filter((column) => column.id !== 'itemId' && column.id !== 'itemCode' && column.id !== 'unitId').map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.numeric ? 'right' : 'left'}
                                            padding={column.disablePadding ? 'none' : 'normal'}
                                            sortDirection={false}
                                            className="font-bold text-center"
                                        >{column.label}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody sx={{ '& tr:nth-child(odd)': { background: `transparent !important` }, '& tr': {  borderBottom: `1px solid #B8BDCB` }, '& tr:last-child': { borderBottom: 0 } }}>
                                {(sr?.stockReceiptItems || []).map((row, index) => {
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    return (
                                        <TableRow hover tabIndex={-1} key={row.id} sx={{ height: '36px' }}>
                                            {columns.filter((column) => column.id !== 'itemId' && column.id !== 'itemCode' && column.id !== 'unitId').map((column) => (
                                                <TableCell
                                                    key={column.id}
                                                    align={column.numeric ? 'right' : 'left'}
                                                    padding={column.disablePadding ? 'none' : 'normal'}
                                                    component={column.id === 'itemName' ? 'th' : undefined}
                                                    id={column.id === 'itemName' ? labelId : undefined}
                                                    scope={column.id === 'itemName' ? 'row' : undefined}
                                                    className="text-center"
                                                >{column.id === 'receivedDate' || column.id === 'expiryDate' 
                                                    ? (row as any)[column.id]
                                                        ?.split(',')
                                                        .map((d: string) => d?.split('T')[0]?.trim())
                                                        .filter(
                                                            (d: string | undefined | null) =>
                                                            d && d !== 'undefined' && d !== 'null' && d !== ''
                                                        )
                                                        .join(', ') || '' 
                                                    : column.id === 'unitTitle' ? ((row as any)[column.id] || t('unit(s)'))
                                                    : (row as any)[column.id]
                                                }</TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                <Box className='flex justify-end'>
                    <Box className="w-[360px]">
                        <Divider className="bg-[#AEB3C4] mb-2" />
                        <Box className='flex justify-between mb-[5px]'>
                            <Typography className="font-Saira text-[12px] text-left">
                                <span className="font-medium">{t('Requested')}: </span> 
                                <span className="italic font-bold ms-1">{sr?.totalQuantity || ''}</span>
                            </Typography>
                            <Typography className="font-Saira text-[12px] text-left">
                                <span className="font-medium">{t('Received')}: </span> 
                                <span className="italic font-bold ms-1">{sr?.totalReceivedQuantity || ''}</span>
                            </Typography>
                        </Box>
                        <Box className='flex justify-between mb-[5px]'>
                            <Typography className="font-Saira text-[12px] text-left flex">
                                <span className="font-medium">{t('Amount')}: </span> 
                                <p className="font-bold ms-1">
                                    <span className="uppercase ms-1 font-semibold" style={{ color: themeColors?.primary }}>{loggedInUserCurrency}</span>
                                    <span className="italic font-bold ms-1">{new Intl.NumberFormat('en-US', {}).format(sr?.totalAmount || 0) || 0}</span>
                                </p>
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default PrintPurchaseOrderStockReceived;